import AccessPointService from './accessPoint';
import LocationService from './location';
import GatewayService from './gateway';
import { DEVICES_TYPE } from '../constants/status';

/**
 * Determina el tipo de dispositivo desde el NodeAddress
 * @param {string} nodeAddress - NodeAddress del dispositivo (e.g., $301$0-0-0-00718bc1d4d)
 * @returns {string|null} - Tipo de dispositivo o null si no se puede determinar
 */
function getDeviceTypeFromNodeAddress(nodeAddress) {
  if (nodeAddress.startsWith('$101$')) return DEVICES_TYPE.gateway;
  if (nodeAddress.startsWith('$301$')) return DEVICES_TYPE.accessPoint;
  if (nodeAddress.startsWith('$501$')) return DEVICES_TYPE.locationBeacon;
  if (nodeAddress.startsWith('$502$')) return DEVICES_TYPE.tag;
  return null;
}

/**
 * Determina el orden de búsqueda óptimo basado en el identificador
 * @param {string} identifier - MAC address o NodeAddress
 * @returns {Array<string>} - Array con el orden de tipos a intentar
 */
function getSearchOrder(identifier) {
  const normalized = identifier.toUpperCase().replace(/[:-]/g, '');
  
  // Si es NodeAddress, usar detección directa
  if (identifier.startsWith('$')) {
    const type = getDeviceTypeFromNodeAddress(identifier);
    if (type) return [type];
  }
  
  // Si es MAC con prefijo C0 o 000030 = probablemente Access Point
  if (normalized.startsWith('C0') || normalized.startsWith('000030')) {
    return [DEVICES_TYPE.accessPoint, DEVICES_TYPE.locationBeacon, DEVICES_TYPE.gateway];
  }
  
  // Para otros prefijos, intentar Location Beacon primero (más común)
  return [DEVICES_TYPE.locationBeacon, DEVICES_TYPE.accessPoint, DEVICES_TYPE.gateway];
}

/**
 * Busca un dispositivo por MAC address o NodeAddress intentando múltiples endpoints
 * @param {string} identifier - MAC address (e.g., C0:07:18:BC:1D:4D) o NodeAddress (e.g., $301$0-0-0-00718bc1d4d)
 * @returns {Promise<{type: string, data: Object}>} - Objeto con tipo y datos del dispositivo
 * @throws {Error} - Si el dispositivo no se encuentra en ningún endpoint
 */
async function searchDeviceByIdentifier(identifier) {
  const searchOrder = getSearchOrder(identifier);
  const errors = [];
  
  for (const deviceType of searchOrder) {
    try {
      let response;
      
      switch (deviceType) {
        case DEVICES_TYPE.accessPoint:
          response = await AccessPointService.getAccessPointById(identifier);
          break;
          
        case DEVICES_TYPE.locationBeacon:
          response = await LocationService.getLocationById(identifier);
          break;
          
        case DEVICES_TYPE.gateway:
          response = await GatewayService.getGatewayById(identifier);
          break;
          
        default:
          continue;
      }
      
      if (response) {
        return { type: deviceType, data: response };
      }
    } catch (error) {
      errors.push({ type: deviceType, error: error.message || error });
      continue;
    }
  }
  
  throw new Error(`Device not found: ${identifier}`);
}

export default {
  searchDeviceByIdentifier,
  getDeviceTypeFromNodeAddress,
  getSearchOrder,
};
